/**

 * Judy header nav — dwell debounce, tray slide-down on open, fade on switch.

 */

class JudyHeaderNav {

  /** @type {HTMLElement | null} */

  #nav = null;



  /** @type {HTMLElement | null} */

  #header = null;



  /** @type {HTMLElement | null} */

  #tray = null;



  /** @type {HTMLElement | null} */

  #backdrop = null;



  /** @type {number | null} */

  #closeTimer = null;



  /** @type {number | null} */

  #dwellTimer = null;



  /** @type {number | null} */

  #switchEndTimer = null;



  /** @type {{ key: string, item: HTMLElement, link: HTMLElement, panel: HTMLElement } | null} */

  #pending = null;



  /** @type {string | null} */

  #activeKey = null;



  /** @type {boolean} */

  #trayOpen = false;



  /** @type {number} */

  #switchGeneration = 0;



  /** @type {number} */

  #fadeInGeneration = 0;



  static TRAY_ENTER_MS = 520;



  static PANEL_FADE_MS = 420;



  static CLOSE_DELAY_MS = 160;



  /** First open: hover must rest before tray opens */

  static DWELL_MS = 220;



  /** Tray open: debounce rapid hovers across nav items */

  static SWITCH_DWELL_MS = 180;



  init() {

    this.#nav = document.querySelector('[data-judy-header-nav]');

    this.#header = document.getElementById('header-component');

    this.#tray = this.#nav?.querySelector('[data-judy-dropdown-tray]') ?? null;

    if (!this.#nav || !this.#header || !this.#tray) return;



    this.#ensureBackdrop();

    this.#updateDropdownTop();

    window.addEventListener('resize', () => this.#updateDropdownTop(), { passive: true });



    if (window.matchMedia('(min-width: 750px)').matches) {

      this.#header.dataset.menuStyle = 'menu';

    }



    const items = this.#nav.querySelectorAll('[data-judy-nav-item]');

    for (const item of items) {

      if (!(item instanceof HTMLElement)) continue;

      const key = item.dataset.judyNavItem;

      const link = item.querySelector('.judy-header-nav__link');

      const panel = key ? this.#getPanel(key) : null;

      if (!key || !(link instanceof HTMLElement) || !(panel instanceof HTMLElement)) continue;



      item.addEventListener('mouseenter', () => this.#scheduleActivation(key, item, link, panel));

      link.addEventListener('focus', () => {

        this.#cancelDwell();

        this.#commitActivation(key, item, link, panel);

      });

    }



    this.#bindHoverPersistence();



    this.#nav.addEventListener('focusout', (event) => {

      const related = event.relatedTarget;

      if (this.#isInsideHoverZone(related)) return;

      this.#cancelDwell();

      this.#scheduleClose(0);

    });



    document.addEventListener('keydown', (event) => {

      if (event.key === 'Escape') this.#closeTray();

    });

  }



  #bindHoverPersistence() {

    const keepOpen = () => this.#cancelClose();



    this.#nav?.addEventListener('mouseenter', keepOpen);

    this.#tray?.addEventListener('mouseenter', keepOpen);



    const maybeClose = (/** @type {MouseEvent} */ event) => {

      if (this.#isInsideHoverZone(event.relatedTarget)) return;

      this.#cancelDwell();

      this.#scheduleClose(JudyHeaderNav.CLOSE_DELAY_MS);

    };



    this.#nav?.addEventListener('mouseleave', maybeClose);

    this.#tray?.addEventListener('mouseleave', maybeClose);

  }



  /**

   * @param {EventTarget | null} target

   */

  #isInsideHoverZone(target) {

    if (!(target instanceof Node)) return false;

    return Boolean(this.#nav?.contains(target) || this.#tray?.contains(target));

  }



  #ensureBackdrop() {

    this.#backdrop = document.querySelector('.judy-header-dropdown-backdrop');

    if (this.#backdrop) return;



    this.#backdrop = document.createElement('div');

    this.#backdrop.className = 'judy-header-dropdown-backdrop';

    this.#backdrop.setAttribute('aria-hidden', 'true');

    this.#backdrop.addEventListener('click', () => this.#closeTray());

    document.body.appendChild(this.#backdrop);

  }



  #setBackdropVisible(visible) {

    this.#ensureBackdrop();

    if (!this.#backdrop) return;



    this.#backdrop.classList.toggle('is-visible', visible);

    this.#backdrop.setAttribute('aria-hidden', visible ? 'false' : 'true');

    document.body.classList.toggle('judy-header-nav-dropdown-open', visible);

  }



  #updateDropdownTop() {

    if (!this.#header) return;

    const height = this.#header.getBoundingClientRect().height;

    document.documentElement.style.setProperty('--judy-nav-dropdown-top', `${height}px`);

  }



  #invalidatePendingAnimations() {

    this.#switchGeneration += 1;

    this.#fadeInGeneration += 1;

    this.#cancelSwitchEnd();

  }



  #cancelSwitchEnd() {

    if (this.#switchEndTimer) {

      clearTimeout(this.#switchEndTimer);

      this.#switchEndTimer = null;

    }

  }



  /**

   * @param {string} key

   * @returns {HTMLElement | null}

   */

  #getPanel(key) {

    return this.#tray?.querySelector(`[data-judy-nav-panel="${CSS.escape(key)}"]`) ?? null;

  }



  /**

   * @param {string} key

   * @param {HTMLElement} item

   * @param {HTMLElement} link

   * @param {HTMLElement} panel

   */

  #scheduleActivation(key, item, link, panel) {

    this.#cancelClose();



    if (this.#trayOpen && this.#activeKey === key) return;



    this.#cancelDwell();



    this.#pending = { key, item, link, panel };



    const delay =

      this.#trayOpen && this.#activeKey !== null ? JudyHeaderNav.SWITCH_DWELL_MS : JudyHeaderNav.DWELL_MS;



    this.#dwellTimer = window.setTimeout(() => {

      this.#dwellTimer = null;

      const pending = this.#pending;

      if (!pending || pending.key !== key) return;

      this.#commitActivation(pending.key, pending.item, pending.link, pending.panel);

    }, delay);

  }



  #cancelDwell() {

    if (this.#dwellTimer) {

      clearTimeout(this.#dwellTimer);

      this.#dwellTimer = null;

    }

    this.#pending = null;

  }



  /**

   * @param {string} key

   * @param {HTMLElement} item

   * @param {HTMLElement} link

   * @param {HTMLElement} panel

   */

  #commitActivation(key, item, link, panel) {

    if (this.#trayOpen && this.#activeKey === key) return;



    this.#cancelClose();

    this.#cancelDwell();

    this.#updateDropdownTop();



    const wasTrayOpen = this.#trayOpen;

    const isSwitch = wasTrayOpen && this.#activeKey !== null && this.#activeKey !== key;



    if (isSwitch) {

      this.#invalidatePendingAnimations();

      this.#nav?.classList.add('is-switching');

    }



    if (!wasTrayOpen) {

      this.#openTray();

    }



    this.#setActiveItem(item, link);

    this.#setActivePanel(panel, { isSwitch, shouldReveal: !wasTrayOpen, key });

    this.#activeKey = key;



    if (isSwitch) {

      const generation = this.#switchGeneration;

      this.#switchEndTimer = window.setTimeout(() => {

        if (generation !== this.#switchGeneration) return;

        this.#nav?.classList.remove('is-switching');

        if (this.#activeKey === key) {

          panel.classList.remove('is-fading-in');

        }

        this.#cleanupLeavingPanels();

        this.#switchEndTimer = null;

      }, JudyHeaderNav.PANEL_FADE_MS);

    }

  }



  /**

   * @param {HTMLElement} item

   * @param {HTMLElement} link

   */

  #setActiveItem(item, link) {

    if (!this.#nav) return;

    for (const el of this.#nav.querySelectorAll('.judy-header-nav__item.is-open')) {

      el.classList.remove('is-open');

      el.querySelector('.judy-header-nav__link')?.setAttribute('aria-expanded', 'false');

    }

    item.classList.add('is-open');

    link.setAttribute('aria-expanded', 'true');

  }



  /**

   * @param {HTMLElement} panel

   */

  #hidePanelInstant(panel) {

    panel.classList.remove('is-active', 'is-entering', 'is-fading-in', 'is-leaving');

    panel.classList.add('is-instant-hide');

    panel.setAttribute('hidden', '');

  }



  #cleanupLeavingPanels() {

    if (!this.#tray) return;



    for (const el of this.#tray.querySelectorAll('.judy-header-nav__dropdown-panel.is-leaving')) {

      if (!(el instanceof HTMLElement)) continue;

      el.classList.remove('is-leaving');

      this.#hidePanelInstant(el);

    }

  }



  /**

   * @param {HTMLElement} panel

   * @param {{ isSwitch: boolean, shouldReveal: boolean, key: string }} options

   */

  #setActivePanel(panel, { isSwitch, shouldReveal, key }) {

    if (!this.#tray) return;



    for (const el of this.#tray.querySelectorAll('.judy-header-nav__dropdown-panel')) {

      if (!(el instanceof HTMLElement) || el === panel) continue;



      if (isSwitch && el.classList.contains('is-leaving')) {

        this.#hidePanelInstant(el);

        el.classList.remove('is-leaving');

        continue;

      }



      if (isSwitch && el.classList.contains('is-active')) {

        el.classList.remove('is-active', 'is-entering', 'is-fading-in');

        el.classList.add('is-leaving');

        continue;

      }



      if (el.classList.contains('is-active') || el.classList.contains('is-instant-hide')) {

        this.#hidePanelInstant(el);

      }

    }



    panel.classList.remove('is-instant-hide', 'is-leaving', 'is-entering', 'is-fading-in');

    panel.removeAttribute('hidden');

    panel.classList.add('is-active');



    if (isSwitch) {

      const fadeGen = ++this.#fadeInGeneration;

      panel.classList.remove('is-fading-in');

      void panel.offsetWidth;

      requestAnimationFrame(() => {

        requestAnimationFrame(() => {

          if (fadeGen !== this.#fadeInGeneration || this.#activeKey !== key) return;

          if (panel.classList.contains('is-active')) {

            panel.classList.add('is-fading-in');

          }

        });

      });

      return;

    }



    if (shouldReveal) {

      void panel.offsetWidth;

      panel.classList.add('is-entering');



      window.setTimeout(() => {

        if (this.#activeKey !== key) return;

        if (panel.classList.contains('is-active')) {

          panel.classList.remove('is-entering');

        }

      }, JudyHeaderNav.TRAY_ENTER_MS + 200);

    }

  }



  #openTray() {

    if (!this.#nav || !this.#tray) return;

    this.#trayOpen = true;

    this.#nav.classList.add('is-tray-open', 'is-tray-entering');

    this.#header?.classList.add('judy-header-nav--dropdown-open');

    this.#tray.removeAttribute('hidden');

    this.#tray.setAttribute('aria-hidden', 'false');

    this.#setBackdropVisible(true);



    window.setTimeout(() => {

      this.#nav?.classList.remove('is-tray-entering');

    }, JudyHeaderNav.TRAY_ENTER_MS);

  }



  /** @param {number} [delay] */

  #scheduleClose(delay = JudyHeaderNav.CLOSE_DELAY_MS) {

    this.#cancelClose();

    this.#closeTimer = window.setTimeout(() => this.#closeTray(), delay);

  }



  #cancelClose() {

    if (this.#closeTimer) {

      clearTimeout(this.#closeTimer);

      this.#closeTimer = null;

    }

  }



  #closeTray() {

    if (!this.#nav || !this.#tray || !this.#trayOpen) return;



    this.#cancelDwell();

    this.#invalidatePendingAnimations();

    this.#trayOpen = false;

    this.#activeKey = null;

    this.#nav.classList.remove('is-tray-open', 'is-tray-entering', 'is-switching');

    this.#header?.classList.remove('judy-header-nav--dropdown-open');

    this.#setBackdropVisible(false);

    this.#tray.setAttribute('aria-hidden', 'true');

    this.#cleanupLeavingPanels();



    for (const item of this.#nav.querySelectorAll('.judy-header-nav__item.is-open')) {

      item.classList.remove('is-open');

      item.querySelector('.judy-header-nav__link')?.setAttribute('aria-expanded', 'false');

    }



    for (const panel of this.#tray.querySelectorAll('.judy-header-nav__dropdown-panel')) {

      if (!(panel instanceof HTMLElement)) continue;

      panel.classList.remove('is-active', 'is-entering', 'is-fading-in', 'is-instant-hide', 'is-leaving');

    }



    window.setTimeout(() => {

      if (this.#trayOpen) return;

      this.#tray?.setAttribute('hidden', '');

      for (const panel of this.#tray?.querySelectorAll('.judy-header-nav__dropdown-panel') ?? []) {

        if (panel instanceof HTMLElement) {

          panel.setAttribute('hidden', '');

        }

      }

    }, JudyHeaderNav.TRAY_ENTER_MS);

  }

}



const judyNav = new JudyHeaderNav();

if (document.readyState === 'loading') {

  document.addEventListener('DOMContentLoaded', () => judyNav.init());

} else {

  judyNav.init();

}


